import React, { useEffect, useState } from "react";
import "./index.css";
import { Button, Input, Space, Tooltip, message } from "antd";
import { DeleteOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import GenericTable from "../../GeneralTable/GenericTable";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppModal from "../../modals/AppModal";

// redux
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setHighlightsProjectId,
  setHighlightsUsername,
  setHighlightsPage,
  setHighlightsPageSize,
  fetchHighlights,
  deleteHighlight,
  fetchHighlightById,
  clearSelectedHighlight,
  clearHighlightsDirty,
} from "./highlights.slice";
import type { HighlightRow } from "./highlights.slice";
import { useExporter } from "../../../hooks/useExporter";

const { Search } = Input;

const Highlights: React.FC = () => {
  const { t } = useTranslation("docProcessing");
  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();

  const {
    items,
    total,
    page,
    pageSize,
    username,
    loading,
    selected,
    selectedLoading,
    dirty,
  } = useAppSelector((s) => s.highlights);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    id: string | null;
    loading: boolean;
  }>({ open: false, id: null, loading: false });

  const { exporting, exportNow } = useExporter({
    endpoint: "highlights/export",
    fallbackPrefix: "highlights",
    ext: "xlsx",
  });

  /* -------------------- effects -------------------- */

  useEffect(() => {
    dispatch(setHighlightsProjectId(projectId ?? null));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!projectId) return;

    if (dirty) {
      dispatch(fetchHighlights()).finally(() => {
        dispatch(clearHighlightsDirty());
      });
    } else {
      dispatch(fetchHighlights());
    }
  }, [dispatch, projectId, page, pageSize, username, dirty]);

  /* -------------------- table config -------------------- */

  const columns: ColumnsType<HighlightRow> = [
    {
      title: t("highlights.table.media"),
      dataIndex: "document_name",
      key: "media",
      sorter: (a, b) =>
        (a.document_name || "").localeCompare(b.document_name || ""),
      render: (text: string) => (
        <Tooltip title={text}>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 250,
            }}
          >
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t("highlights.table.date"),
      dataIndex: "created_at",
      key: "date",
      sorter: (a, b) =>
        new Date(a.created_at || "").getTime() -
        new Date(b.created_at || "").getTime(),
      render: (text: string) => <span>{(text || "").split("T")[0]}</span>,
    },
    {
      title: t("highlights.table.username"),
      dataIndex: "username",
      key: "username",
      sorter: (a, b) => (a.username || "").localeCompare(b.username || ""),
    },
    {
      title: t("highlights.table.length"),
      dataIndex: "length",
      key: "length",
      sorter: (a, b) => {
        const parse = (t: string) => Number(String(t).replace(/\D+/g, "")) || 0;
        return parse(a.length) - parse(b.length);
      },
    },
    {
      title: t("highlights.table.codesCount"),
      dataIndex: "codesCount",
      key: "codesCount",
      sorter: (a, b) => (a.codesCount || 0) - (b.codesCount || 0),
    },
    {
      key: "action",
      className: "actionColumn",
      render: (_: any, record: HighlightRow) => (
        <Space size="middle" onClick={(e) => e.stopPropagation()}>
          <EyeOutlined
            style={{ color: "#A072EB", cursor: "pointer" }}
            onClick={() => dispatch(fetchHighlightById(record.id))}
          />
          <DeleteOutlined
            style={{ color: "#A072EB", cursor: "pointer" }}
            onClick={() =>
              setDeleteModal({ open: true, id: record.id, loading: false })
            }
          />
        </Space>
      ),
    },
  ];

  const pagination = {
    current: page,
    pageSize,
    total,
    showSizeChanger: false,
    onChange: (p: number) => {
      dispatch(setHighlightsPage(p));
    },
  };

  /* -------------------- actions -------------------- */

  const handleConfirmDelete = async () => {
    if (!deleteModal.id) return;
    try {
      setDeleteModal((s) => ({ ...s, loading: true }));
      await dispatch(deleteHighlight(deleteModal.id)).unwrap();
      message.success(t("highlights.deleteSuccess"));
    } finally {
      setDeleteModal({ open: false, id: null, loading: false });
    }
  };

  const handleExport = async () => {
    const pid = projectId;
    if (!pid) return;
    await exportNow({ params: { project_id: pid } });
  };

  /* -------------------- render -------------------- */

  return (
    <div className="highlight-container">
      <div className="highlight-header">
        <h2>{t("highlights.totalHighlights", { value: total })}</h2>
        <Button
          className="exportbtn"
          onClick={handleExport}
          disabled={!projectId}
          loading={exporting}
        >
          <UploadOutlined className="exportIcon" />
          {t("highlights.export")}
        </Button>
      </div>

      {!selected ? (
        <>
          <div className="filters">
            <Search
              placeholder={t("highlights.searchByUsername")}
              allowClear
              style={{ width: 300 }}
              className="search-by-username-input"
              value={username}
              onChange={(e) => dispatch(setHighlightsUsername(e.target.value))}
              onSearch={(v) => dispatch(setHighlightsUsername(v))}
            />
          </div>
          <div className="tableWrap">
            <GenericTable<HighlightRow>
              rowSelection={{
                selectedRowKeys,
                onChange: (ks) => setSelectedRowKeys(ks),
              }}
              columns={columns}
              dataSource={items}
              loading={loading}
              pagination={pagination}
              style={{ width: "100%" }}
              // scroll={{ y: 300 }}
              emptyText={t("highlights.emptyText")}
              onRowClick={(r) => dispatch(fetchHighlightById(r.id))}
            />
          </div>
        </>
      ) : (
        <div
          className="highlight-details-container"
          style={{ display: "flex", gap: "2rem" }}
        >
          <div
            className="highlight-details"
            style={{
              width: "60%",
              border: "1px solid #F4D9FF",
              borderRadius: "5px",
            }}
          >
            <div
              className="highlight-meta"
              style={{
                display: "flex",
                gap: "2rem",
                alignItems: "center",
                backgroundColor: "#F5F5F5",
                padding: "0.5rem 1rem",
              }}
            >
              <p
                style={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  color: "#808080",
                  fontWeight: 600,
                }}
              >
                <span>{t("highlights.added")}</span>{" "}
                {(selected.created_at || "").split("T")[0]}
              </p>
              <p
                style={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  color: "#808080",
                  fontWeight: 600,
                }}
              >
                <strong>{t("highlights.username")}</strong> {selected.username}
              </p>
              <p
                style={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  color: "#808080",
                  fontWeight: 600,
                }}
              >
                <strong>{t("highlights.codes")}</strong> {selected.codesCount}
              </p>
            </div>
            <div className="highlight-body" style={{ padding: "0.5rem 1rem" }}>
              <h3
                style={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "black",
                }}
              >
                {t("highlights.resource", { value: selected.document_name })}
              </h3>
              <p
                style={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#808080",
                }}
              >
                {selected.content}
              </p>
            </div>
            <div style={{ padding: "0.5rem 1rem" }}>
              <Button
                style={{
                  height: "44px",
                  border: "1px solid #4A1C96",
                  color: "#4A1C96",
                  fontFamily: "Poppins",
                }}
                onClick={() => dispatch(clearSelectedHighlight())}
              >
                {t("highlights.back")}
              </Button>
            </div>
          </div>

          <div
            className="card"
            style={{
              width: "40%",
              backgroundColor: "#FDF8FF",
              border: "1px solid #F4D9FF",
              borderRadius: "5px",
            }}
          >
            <h1
              style={{
                fontFamily: "Poppins",
                fontSize: 18,
                fontWeight: 500,
                color: "#4A1C96",
              }}
            >
              {t("highlights.attachedCodes")}
            </h1>
            <div>
              {(selected.code_name ?? []).map((code: any, i: number) => (
                <div
                  key={i}
                  className="code-item"
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "#F4D9FF",
                    border: "1px solid #F4D9FF",
                    borderRadius: "8px",
                    marginBottom: "0.5rem",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontSize: 14,
                      color: "#A072EB",
                    }}
                  >
                    {code}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <AppModal
        open={deleteModal.open}
        loading={deleteModal.loading}
        onCancel={() =>
          setDeleteModal({ open: false, id: null, loading: false })
        }
        onConfirm={handleConfirmDelete}
        title={t("highlights.deleteModal.title")}
        description={t("highlights.deleteModal.description")}
        subDescription={t("highlights.deleteModal.subDescription")}
        confirmText={t("highlights.deleteModal.confirm")}
        cancelText={t("highlights.deleteModal.cancel")}
        confirmType="primary"
        danger
        className="delete-confirmation-modal"
      />
    </div>
  );
};

export default Highlights;
